/**
 * Dev mode's store: a private copy of the garden that plays by the real rules
 * (the same engine the server runs) but never sends anything to the server.
 * It keeps its own clock, so days can be skipped, and the dev panel can change
 * things directly to set up a scene. Saved in this browser only.
 */
import {
  applyAction, DEFAULT_RULES, GameError, GROWTH_STAGES, newGame, runCron,
  type Action, type Context, type GameState, type GrowthStage, type Notice, type Rules, type StarterBiome,
} from '@thicket/core';
import { request } from '../api.js';
import type { GameStore, StoreDeps } from '../store.js';

const KEY = 'thicket.dev.sandbox';
const DAY = 86_400_000;

interface Saved {
  state: GameState;
  rules: Rules;
  /** Days skipped ahead of real time. */
  offset: number;
}

export class SandboxStore implements GameStore {
  view: GameState | null = null;
  rules: Rules = DEFAULT_RULES;
  readonly queue: readonly unknown[] = [];
  readonly offline = false;
  offsetDays = 0;
  private listeners = new Set<() => void>();

  constructor(private deps: StoreDeps) {}

  now(): Date {
    return new Date(this.deps.now().getTime() + this.offsetDays * DAY);
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  private ctx(): Context {
    return { now: this.now(), newId: this.deps.uuid, random: Math.random, rules: this.rules, source: 'app' };
  }

  private save() {
    if (!this.view) return;
    try {
      this.deps.storage?.setItem(KEY, JSON.stringify({ state: this.view, rules: this.rules, offset: this.offsetDays } satisfies Saved));
    } catch {
      // Too big or blocked: the sandbox still works until the page closes.
    }
  }

  private take(result: { state: GameState; notices: Notice[] }, quiet = false) {
    this.view = result.state;
    if (!quiet && result.notices.length) this.deps.onNotices(result.notices);
    this.save();
    this.emit();
  }

  async load(): Promise<void> {
    try {
      const saved = JSON.parse(this.deps.storage?.getItem(KEY) ?? 'null') as Saved | null;
      if (saved?.state) {
        this.rules = saved.rules ?? DEFAULT_RULES;
        this.offsetDays = saved.offset ?? 0;
        this.view = saved.state;
        this.take(runCron(this.view, this.ctx()), true);
        return;
      }
    } catch {
      // A broken save: start again from the real garden.
    }
    await this.copyReal();
  }

  /** Starts over from a copy of the real garden as it is on the server now. */
  async copyReal(): Promise<void> {
    const [rules, loaded] = await Promise.all([
      request<Rules>(this.deps.fetch, '/api/rules').catch(() => DEFAULT_RULES),
      request<{ state: GameState }>(this.deps.fetch, '/api/state'),
    ]);
    this.rules = rules;
    this.offsetDays = 0;
    this.view = loaded.state;
    this.take(runCron(this.view, this.ctx()), true);
  }

  /** Starts over with a brand-new garden, as a new player would see it. */
  fresh(starterBiome: StarterBiome) {
    this.offsetDays = 0;
    const tz = this.view?.profile.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.take({ state: newGame({ timezone: tz, starterBiome }, this.ctx()), notices: [] });
  }

  act(input: Action): boolean {
    if (!this.view) return false;
    const action = structuredClone(input);
    if (action.type === 'createTask' && !action.task.id) action.task.id = this.deps.uuid();
    try {
      this.take(applyAction(this.view, action, this.ctx()));
      return true;
    } catch (err) {
      this.deps.onMessage(err instanceof GameError ? err.message : 'That didn’t work');
      return false;
    }
  }

  /** Moves the clock forward and runs the daily reset for each day passed. */
  skipDays(days: number) {
    if (!this.view) return;
    for (let i = 0; i < days; i++) {
      this.offsetDays += 1;
      this.take(runCron(this.view!, this.ctx()));
    }
  }

  /** Changes the sandbox directly, outside the rules, to set up a scene. */
  cheat(fn: (s: GameState) => void) {
    if (!this.view) return;
    const s = structuredClone(this.view);
    fn(s);
    this.take({ state: s, notices: [] });
  }

  /** Sets a plant's growth stage, with the completions that stage takes, so the next tap grows on from there. */
  setStage(plantId: string, stage: GrowthStage) {
    this.cheat((s) => {
      const p = s.plants.find((x) => x.id === plantId);
      if (!p) return;
      const i = GROWTH_STAGES.indexOf(stage);
      p.stage = stage;
      p.completions = i === 0 ? 0 : this.rules.stageCounts[p.pace][i - 1]!;
    });
  }

  async refresh(): Promise<void> {
    if (this.view) this.take(runCron(this.view, this.ctx()));
  }

  async flush(): Promise<void> {}

  onRemoteVersion(): void {}
}
